// SidebarBest.js
import React, { useContext, useEffect, useState } from "react";

import { GalleryVerticalEnd, ChevronsUpDown, LogOut, Menu } from 'lucide-react';
import { getDashboardSidebarLinks, DASHBOARD_SIDEBAR_BOTTOM_LINKS } from '../../lib/consts/navigation';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import classnames from 'classnames';
import { UserContext } from "../../context/UserContext";
import { getVenueBrandName } from "../../lib/consts/venueTypes";
import axios from 'axios';

function normalizeRoleLabel(role) {
  const raw = (role || '').toString().toLowerCase();
  const norm = raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '');
  if (norm.includes('superadmin') || norm.includes('superadministr')) return 'Super Admin';
  if (norm === 'owner' || norm === 'propietario') return 'Propietario';
  if (norm === 'staff' || norm === 'personal') return 'Personal';
  return role;
}

// Estos dos contadores viven en el sidebar, o sea que se pollean en TODAS las páginas,
// para todos los usuarios logueados, permanentemente. A 10 s eran ~12 peticiones/minuto
// por sesión, y cada una arrastra la query de autenticación (que carga el usuario entero).
// Era, con diferencia, la mayor fuente de carga de la API.
//
// A 60 s se mantiene la frescura porque el refresco de verdad es por eventos: al enfocar
// la ventana, al volver a la pestaña, y —para preinscripciones— por el bus
// `prereg-count-updated` que dispara la propia página al aprobar/rechazar.
const PREREG_COUNT_POLL_MS = 60000;
const CARTS_COUNT_POLL_MS = 60000;

export default function SidebarBest({ isOpen, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [token, setToken, , venueId, , , , accessPrivileges, , , , venueType] = useContext(UserContext);
  const userRole = accessPrivileges || null;
  const brandName = getVenueBrandName(venueType);

  const [preregCount, setPreregCount] = useState(0);
  const [cartsCount, setCartsCount] = useState(0);

  useEffect(() => {
    if (!venueId || !token) return;

    const refreshPreregCount = () => {
      axios.get(`/v1/venues/${venueId}/pre-registrations/count`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, max-age=0',
          Pragma: 'no-cache',
        },
        params: { _ts: Date.now() },
      }).then(r => setPreregCount(r.data.pending || 0)).catch(() => {});
    };

    const handlePreregCountUpdated = (event) => {
      const nextPending = Number(event?.detail?.pending);
      if (!Number.isNaN(nextPending)) {
        setPreregCount(nextPending);
        return;
      }
      refreshPreregCount();
    };

    const handleWindowFocus = () => refreshPreregCount();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshPreregCount();
      }
    };

    refreshPreregCount();
    const interval = setInterval(() => {
      // Pestaña en segundo plano: no gastar peticiones. Al volver, el listener de
      // `visibilitychange` de abajo refresca al instante, así que no se pierde frescura.
      if (document.visibilityState !== 'visible') return;
      refreshPreregCount();
    }, PREREG_COUNT_POLL_MS);

    window.addEventListener('prereg-count-updated', handlePreregCountUpdated);
    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      clearInterval(interval);
      window.removeEventListener('prereg-count-updated', handlePreregCountUpdated);
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [venueId, token, location.pathname]);

  useEffect(() => {
    if (!venueId || !token) return;

    const refreshCartsCount = () => {
      axios.get('/v1/venue-draft-carts', {
        params: { venue_id: venueId, _ts: Date.now() },
        headers: {
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, max-age=0',
          Pragma: 'no-cache',
        },
      })
        .then((r) => setCartsCount(Array.isArray(r.data?.draft_carts) ? r.data.draft_carts.length : 0))
        .catch(() => {});
    };

    const handleWindowFocus = () => refreshCartsCount();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshCartsCount();
      }
    };

    refreshCartsCount();
    const interval = setInterval(() => {
      // Igual que el contador de preinscripciones: en segundo plano no se pollea.
      if (document.visibilityState !== 'visible') return;
      refreshCartsCount();
    }, CARTS_COUNT_POLL_MS);

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [venueId, token, location.pathname]);

  const badgeMap = { preregCount, cartsCount };

  const handleLogout = () => {
    localStorage.removeItem('kongToken');
    setToken(null);
    navigate('/login');
    onClose();
  };

  const allowedLinks = getDashboardSidebarLinks(venueType).filter(link =>
    (link.divider || link.enabled !== false) && (!link.roles || link.roles.includes(userRole))
  );
  const allowedBottomLinks = DASHBOARD_SIDEBAR_BOTTOM_LINKS.filter(
    link => !link.roles || link.roles.includes(userRole)
  );

  if (!userRole) return null; // or show a loading spinner
  


  return (
    <>
      {/* Mobile backdrop */}
      <div
        className={classnames(
          "fixed inset-0 bg-black bg-opacity-50 z-[50] transition-opacity md:hidden",
          { 'opacity-100': isOpen, 'opacity-0 pointer-events-none': !isOpen }
        )}
        onClick={onClose}
      />

      {/* Sidebar drawer - z-[60] para quedar por encima del banner del socio en Dispensario/Productos */}
      <aside
        className={classnames(
          "fixed inset-y-0 left-0 z-[60] flex min-h-0 w-64 flex-col bg-[var(--sidebar)] shadow transform transition-transform duration-200 ease-in-out",
          { 'translate-x-0': isOpen, '-translate-x-full': !isOpen },
          'md:translate-x-0 md:static md:shadow-none md:h-full'
        )}
      >
        {/* Mobile header */}
        <div className="flex shrink-0 items-center justify-between px-4 py-3 md:hidden">
          <span className="text-lg font-semibold">Menu</span>
          <button onClick={onClose}>
            <Menu size={24} />
          </button>
        </div>

        {/* Desktop header — icon tile + brand + role subtitle (Figma Club_Sidebar header) */}
        <div className="hidden shrink-0 border-b border-[#E5E5E5] p-2 md:block">
          <div className="flex items-center gap-2 rounded-md p-2">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[10px] bg-[#171717] text-white">
              <GalleryVerticalEnd size={16} />
            </div>
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-semibold leading-5 text-[#0A0A0A]">{brandName}</span>
              {userRole && (
                <span className="truncate text-xs leading-4 text-[#0A0A0A]/70">{normalizeRoleLabel(userRole)}</span>
              )}
            </div>
            <ChevronsUpDown size={16} className="flex-shrink-0 text-[#0A0A0A]/60" />
          </div>
        </div>

        {/* Links: ocupa el espacio sobrante y hace scroll; el pie (logout) queda fijo abajo en móvil */}
        <nav className="mt-2 min-h-0 flex-1 overflow-y-auto px-2">
          {allowedLinks.map(item => (
            item.divider ? (
              <hr key={item.key} className="my-2 border-[#E5E5E5]" />
            ) : (
              <SidebarLink
                key={item.key}
                item={item}
                onClick={onClose}
                badge={item.badge ? badgeMap[item.badge] : 0}
              />
            )
          ))}
        </nav>

        {/* Bottom links + logout */}
        <div className="shrink-0 border-t border-[#E5E5E5] bg-[var(--sidebar)] px-2 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          {allowedBottomLinks.map(item => (
            <SidebarLink
              key={item.key}
              item={item}
              onClick={onClose}
            />
          ))}
          <button
            onClick={handleLogout}
            className={classnames(
              "flex items-center w-full gap-2 px-2 py-2 mt-1 rounded-md text-sm text-[#DC2626]",
              "hover:bg-[var(--sidebar-hover)]"
            )}
          >
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
}

function SidebarLink({ item, onClick, badge = 0 }) {
  const { pathname, search } = useLocation();
  const q = new URLSearchParams(search);

  const isActive = (() => {
    if (item.key === 'product') {
      return pathname === '/dispenser' && q.get('mode') === 'productos';
    }
    if (item.key === 'dispenser') {
      return pathname === '/dispenser' && q.get('mode') !== 'productos';
    }
    const basePath = item.path.split('?')[0];
    return pathname === basePath && !item.path.includes('?');
  })();

  return (
    <Link
      to={item.path}
      onClick={onClick}
      className={classnames(
        "flex items-center gap-2 px-2 py-2 rounded-md text-sm",
        // Figma Club_Menu_button (3006:6938): active = filled slate pill + white
        // label; hover = slate-200 tint, and only while NOT active (otherwise the
        // light hover would wash out the dark pill). Same for every venue type.
        isActive
          ? 'bg-[var(--sidebar-active)] text-[var(--primary-foreground)] font-medium'
          : 'text-[var(--sidebar-foreground)] hover:bg-[var(--sidebar-hover)]'
      )}
    >
      <span className="text-lg">{item.icon}</span>
      <span className="truncate flex-1">{item.label}</span>
      {badge > 0 && (
        <span className="ml-auto flex-shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </Link>
  );
}
